/**
 * Copyright (c) 2024 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { colors, mixins } from '@blend/consumer-components';
import { MaterialIcon } from '@blend/enterprise-components';
import ClosingDocument from '../../../common/Documents/ClosingDocument';

export namespace Document {
  export interface Props {
    isSelected: boolean;
    document: ClosingDocument;
    handleClick: (document: ClosingDocument) => void;
  }
}

const buttonWidth = 24;

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    color: colors.catalyst,
    backgroundColor: colors.white,
    width: '100%',
    ...mixins.padding(12, 8),
    ...mixins.borderBottom(1, 'solid', mixins.globalBorderColor),
  },
  selectedWrapper: {
    backgroundColor: colors.reflexLight08,
  },
  buttonWrapper: {
    background: 'none',
    border: 'none',
    textAlign: 'left',
    width: `calc(100% - ${buttonWidth}px)`,
    ...mixins.padding(0),
  },
  name: {
    fontWeight: 'bold',
    fontSize: 16,
    lineHeight: '22px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    cursor: 'pointer',
  },
  statusWrapper: {
    width: '100%',
    display: 'flex',
  },
  status: {
    fontSize: 12,
    color: colors.catalyst,
    lineHeight: '16px',
    ...mixins.marginVertical(3),
    ...mixins.marginHorizontal(8),
  },
  statusDot: {
    ...mixins.marginVertical(6),
    display: 'flex',
    borderRadius: '50%',
    width: 8,
    height: 8,
  },
  tagged: {
    backgroundColor: colors.mint,
  },
  pending: {
    backgroundColor: colors.orange,
  },
  dragIndicator: {
    display: 'flex',
    alignItems: 'center',
    ...mixins.margin(0, 5, 0, 0),
  },
});

export const Document: React.FC<Document.Props> = ({ isSelected, document, handleClick }) => {
  const { name } = document;

  return (
    <div className={css(styles.wrapper, isSelected && styles.selectedWrapper)} data-it={`Document_${document.id}`}>
      <div className={css(styles.dragIndicator)}>
        <MaterialIcon color={colors.catalystLight40} size={24} type="drag_indicator"></MaterialIcon>
      </div>
      <button className={css(styles.buttonWrapper)} data-it="document-button" onClick={() => handleClick(document)}>
        <div className={css(styles.name)} data-it="document-name">
          {name}
        </div>
      </button>
    </div>
  );
};
